import type { UseFieldArrayRemove } from 'react-hook-form';

import { FilterParts } from '@entities/dadata';

import {
  DadataFioController,
  DatepickerController,
  DynamicFormTitle,
  InputController,
  type Option,
  SelectController,
} from '@shared/ui';
import { getDateForAge } from '@shared/utils';

import { useTranslation } from '../../../helpers';
import {
  FieldHint,
  FieldsGrid,
  MiddleNameField,
  RelativeContainer,
} from './step-rgs-relatives.styles';
import type { StepRgsRelativesValues } from './types';

interface StepRgsRelativeFieldProps {
  index: number;
  isRelationshipTypeFetching: boolean;
  onRemove?: UseFieldArrayRemove;
  relationshipTypeOptions: Option[];
}

const minBirthDate = getDateForAge(100);
const maxBirthDate = new Date();

export const StepRgsRelativeField = ({
  index,
  isRelationshipTypeFetching,
  onRemove,
  relationshipTypeOptions,
}: StepRgsRelativeFieldProps) => {
  const t = useTranslation();

  return (
    <RelativeContainer>
      <DynamicFormTitle
        title={t('rgsEmployeeRelativesForm.fieldsetTitle')}
        index={index}
        buttonProps={onRemove ? { onClick: onRemove } : undefined}
        marginBottom={0}
        isNewPath={true}
        testId='relative'
      />
      <FieldsGrid>
        <SelectController<StepRgsRelativesValues>
          name={`relativesDetails.${index}.relationshipType`}
          label={t('rgsEmployeeRelativesForm.labels.relationship')}
          options={relationshipTypeOptions}
          disabled={isRelationshipTypeFetching}
          testId={`relative-${index}-relationship`}
        />
        <DadataFioController<StepRgsRelativesValues>
          name={`relativesDetails.${index}.lastName`}
          filterParts={[FilterParts.SURNAME]}
          id={`relative-${index}-lastName`}
          label={t('rgsEmployeeRelativesForm.labels.lastName')}
          testId={`relative-${index}-lastName`}
        />
        <DadataFioController<StepRgsRelativesValues>
          name={`relativesDetails.${index}.firstName`}
          filterParts={[FilterParts.NAME]}
          id={`relative-${index}-firstName`}
          label={t('rgsEmployeeRelativesForm.labels.firstName')}
          testId={`relative-${index}-firstName`}
        />
        <MiddleNameField>
          <DadataFioController<StepRgsRelativesValues>
            name={`relativesDetails.${index}.middleName`}
            filterParts={[FilterParts.PATRONYMIC]}
            id={`relative-${index}-middleName`}
            label={t('rgsEmployeeRelativesForm.labels.middleName')}
            testId={`relative-${index}-middleName`}
          />
          <FieldHint>
            {t('rgsEmployeeRelativesForm.hints.middleName')}
          </FieldHint>
        </MiddleNameField>
        <DatepickerController<StepRgsRelativesValues>
          name={`relativesDetails.${index}.birthDate`}
          label={t('rgsEmployeeRelativesForm.labels.birthDate')}
          id={`relative-${index}-birthDate`}
          minDate={minBirthDate}
          maxDate={maxBirthDate}
          showMonthDropdown
          showYearDropdown
          showInput
          testId={`relative-${index}-birthDate`}
        />
        <InputController<StepRgsRelativesValues>
          name={`relativesDetails.${index}.phone`}
          label={t('rgsEmployeeRelativesForm.labels.phone')}
          id={`relative-${index}-phone`}
          type='tel'
          testId={`relative-${index}-phone`}
        />
      </FieldsGrid>
    </RelativeContainer>
  );
};
